import React from 'react';
// Generic skeleton loader components
export const SkeletonCard = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-slate-700 rounded-lg ${className}`}></div>
);
export const SkeletonText = ({ lines = 1, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <SkeletonCard
        key={i}
        className={`h-4 ${i === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full'}`}
      />
    ))}
  </div>
);
export const SkeletonTable = ({ rows = 5, columns = 4 }) => (
  <div className="animate-pulse">
    {/* Table Header */}
    <div className="flex space-x-4 mb-4">
      {Array.from({ length: columns }).map((_, i) => (
        <SkeletonCard key={i} className="h-8 flex-1" />
      ))}
    </div>
    {/* Table Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="flex space-x-4 mb-3">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <SkeletonCard
            key={colIndex}
            className={`h-12 flex-1 ${colIndex === 0 ? 'w-1/4' : 'w-full'}`}
          />
        ))}
      </div>
    ))}
  </div>
);
export const SkeletonStats = ({ count = 4 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border dark:border-slate-700">
        <div className="animate-pulse">
          <SkeletonCard className="h-4 w-20 mb-2" />
          <SkeletonCard className="h-8 w-16 mb-4" />
          <SkeletonCard className="h-3 w-24" />
        </div>
      </div>
    ))}
  </div>
);
export const SkeletonForm = ({ fields = 6 }) => (
  <div className="space-y-6 animate-pulse">
    {Array.from({ length: fields }).map((_, i) => (
      <div key={i} className="space-y-2">
        <SkeletonCard className="h-4 w-24" />
        <SkeletonCard className="h-10 w-full" />
      </div>
    ))}
    <div className="flex space-x-4 pt-4">
      <SkeletonCard className="h-10 w-24" />
      <SkeletonCard className="h-10 w-20" />
    </div>
  </div>
);
// Full page skeleton for app navigation loading (without sidebar)
export const PageNavigationSkeleton = () => {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 250);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <div className="w-full">
      {/* Main Content Skeleton */}
      <div className="p-0 sm:p-2">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div>
              <SkeletonCard className="h-8 w-48 mb-2" />
              <SkeletonCard className="h-4 w-64" />
            </div>
            <div className="flex space-x-3">
              <SkeletonCard className="h-10 w-24" />
              <SkeletonCard className="h-10 w-32" />
            </div>
          </div>
          {/* Stats Cards */}
          <SkeletonStats count={4} />
          {/* Content Area */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border dark:border-slate-700 p-6">
            <div className="space-y-4">
              {/* Table Header */}
              <div className="flex justify-between items-center">
                <SkeletonCard className="h-6 w-32" />
                <SkeletonCard className="h-8 w-24" />
              </div>
              {/* Table */}
              <SkeletonTable rows={8} columns={5} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PageSkeleton = ({ children, loading, skeleton }) => {
  const [showSkeleton, setShowSkeleton] = React.useState(false);

  React.useEffect(() => {
    let timer;
    if (loading) {
      // Delay skeleton appearance to avoid flash on fast reloads
      timer = setTimeout(() => setShowSkeleton(true), 200);
    } else {
      setShowSkeleton(false);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  if (loading) {
    return showSkeleton ? skeleton : null;
  }
  return children;
};
